/**
 * Shared engine primitives for the module system.
 *
 * These were lifted verbatim from `src/lib/mapper/modules/catalog.ts` so that both
 * the per-module descriptors (`src/modules/<kind>/module.server.ts`) and the
 * backward-compat `catalog.ts` façade read them from one place. Pure,
 * server-safe, dependency-light. Nothing here imports a `*.client` file.
 *
 * See docs/superpowers/specs/2026-06-06-componentization-and-module-contract-design.md
 */

import { defaultHelpers } from "@/lib/mapper/helpers";
import type {
  HelperRegistry,
  LookupDictionary,
  ShopifyRow,
  SourceRow,
} from "@/lib/mapper/types";

/**
 * Sentinel written into a cell where an "ai" module should later be filled by
 * the preview/export route. Chosen to be unlikely in real data.
 */
export const AI_RULE_PLACEHOLDER = " AI_PENDING ";

function parseLooseNumber(s: string): number | null {
  const direct = Number(String(s).trim());
  if (Number.isFinite(direct)) return direct;
  let cleaned = ""; let dot = false;
  for (const ch of String(s)) {
    if (ch >= "0" && ch <= "9") cleaned += ch;
    else if (ch === "." && !dot) { cleaned += ch; dot = true; }
    else if (ch === "-" && cleaned === "") cleaned += ch;
  }
  const n = Number(cleaned);
  return /[0-9]/.test(cleaned) && Number.isFinite(n) ? n : null;
}

function arithmetic(a: string, b: string, op: (x: number, y: number) => number): string {
  const x = parseLooseNumber(a ?? "");
  const y = parseLooseNumber(b ?? "");
  if (x === null || y === null) return "";
  const r = op(x, y);
  if (!Number.isFinite(r)) return "";
  return Number.isInteger(r) ? String(r) : String(r);
}

/**
 * Built-in computed `fn` registry. Each fn receives the pre-expanded string
 * args. Helper registry is passed through so a fn like `slugify` can delegate
 * to the matching helper. Returns "" for any arg-shape mismatch — never throws.
 */
export const computedFns: Record<
  string,
  (args: string[], helpers: HelperRegistry) => string
> = {
  // slugify(text, style?, maxLength?) — style "plain" keeps every word; see helpers.ts.
  slugify: (args, helpers) =>
    (helpers.slugify ?? defaultHelpers.slugify)(args[0] ?? "", args[1] ?? "", args[2] ?? ""),
  upper: (args, helpers) => (helpers.upper ?? defaultHelpers.upper)(args[0] ?? ""),
  lower: (args, helpers) => (helpers.lower ?? defaultHelpers.lower)(args[0] ?? ""),
  title: (args, helpers) => (helpers.title ?? defaultHelpers.title)(args[0] ?? ""),
  trim: (args, helpers) => (helpers.trim ?? defaultHelpers.trim)(args[0] ?? ""),
  numberOnly: (args, helpers) =>
    (helpers.numberOnly ?? defaultHelpers.numberOnly)(args[0] ?? ""),
  concat: (args) => args.join(""),
  join: (args) => {
    if (args.length === 0) return "";
    const [sep, ...rest] = args;
    return rest.join(sep);
  },
  prefix: (args) => `${args[1] ?? ""}${args[0] ?? ""}`,
  suffix: (args) => `${args[0] ?? ""}${args[1] ?? ""}`,
  add: (args) => arithmetic(args[0], args[1], (a, b) => a + b),
  sub: (args) => arithmetic(args[0], args[1], (a, b) => a - b),
  mul: (args) => arithmetic(args[0], args[1], (a, b) => a * b),
  div: (args) => arithmetic(args[0], args[1], (a, b) => (b === 0 ? NaN : a / b)),
  round: (args) => {
    const n = parseLooseNumber(args[0] ?? "");
    return n === null ? "" : String(Math.round(n));
  },
  numberFormat: (args) => {
    // keep digits and a single dot (preserves trailing decimals unlike numberOnly's int collapse)
    const s = args[0] ?? "";
    let out = ""; let dot = false;
    for (const ch of s) {
      if (ch >= "0" && ch <= "9") out += ch;
      else if (ch === "." && !dot) { out += ch; dot = true; }
    }
    return out;
  },
};

/** Context passed to a cell module's resolve(). */
export type ResolveCtx = {
  row: SourceRow;
  helpers: HelperRegistry;
  /** Partial output row built so far (topological order). Enables {{out:Field}}. */
  output?: ShopifyRow;
  /** slug → dictionary, injected by the caller. Enables the Value Mapper module. */
  dictionaries?: Record<string, LookupDictionary>;
};
